import { useCallback, useEffect, useMemo, useState } from 'react';
import { Confirm, Mark, SignIn, ThemeToggle, toast, type Destructive } from '@drbell/ui';
import { encodeQr, qrSvg, qrSvgPath } from '@drbell/core';
import { api, ApiError } from '../api.ts';

/**
 * /partners — the partner portal, docs/26 §2.10.
 *
 * A route area of the care site rather than a Worker of its own, so it
 * works today on care.drbell.app/partners; partners.drbell.app can be
 * pointed at it later (docs/26, "What was built"). Same sign-in as the rest
 * of DrBell: a partner who also uses the app has one account.
 *
 * What a partner sees is numbers and money, never people: clicks, sign-ups,
 * paying subscribers, commission, and a statement whose subscribers are
 * pseudonyms (`#A7F3`). The API never sends anything else, so there is
 * nothing here to hide.
 *
 * The QR code is drawn here, from `@drbell/core`'s own encoder, as inline
 * SVG: the site's Content-Security-Policy loads scripts from itself only,
 * and a QR library from a CDN would not run.
 */

type Money = Record<string, number>;

interface Profile {
  id: string;
  displayName: string;
  kind: string;
  status: 'invited' | 'active' | 'paused' | 'terminated';
  depth: number;
  parentName: string | null;
  country: string | null;
  payingEntity: 'newtact' | 'thaja';
  commissionable: boolean;
  poolBps: number | null;
  sliceBps: number | null;
  durationMonths: number;
  holdDays: number;
  phoneAlias: boolean;
  emailAlias: boolean;
}

interface Invitation { id: string; displayName: string; invitedBy: string; depth: number; sliceBps: number | null; poolBps: number | null }

interface Mine {
  partner: Profile | null;
  codes: Array<{ code: string; kind: string; label: string | null; active: boolean; link: string }>;
  link: string | null;
  contact?: { phone: string | null; email: string | null };
  invitations: Invitation[];
  payoutsEnabled: boolean;
}

interface Summary {
  clicks: number;
  signUps: number;
  subscribers: number;
  commission: { pending: Money; approved: Money; paid: Money };
  subPartners: Array<{ id: string; displayName: string; status: string; sliceBps: number | null; clicks: number; signUps: number; subscribers: number; yourShare: Money }>;
}

interface Line {
  id: string; at: number; kind: 'commission' | 'reversal'; role: 'direct' | 'override'; subscriber: string | null; via: string | null;
  tier: string | null; currency: string; basisMinor: number; rateBps: number; amountMinor: number; status: string; holdUntil: number;
}

interface Sub { id: string; displayName: string; status: string; sliceBps: number | null; invitedTo: string | null; createdAt: number }

const field = 'min-h-11 rounded-lg border border-(--line) bg-(--bg) px-3 text-sm';
const button = 'min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent) disabled:opacity-50';
const primary = 'min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-semibold text-white disabled:opacity-50';
const DAY = 86_400_000;
const day = (ms: number) => new Date(ms).toLocaleDateString(undefined, { dateStyle: 'medium' });
const pct = (bps: number | null) => (bps === null ? '—' : `${bps / 100}%`);

function money(minor: number, currency: string): string {
  try {
    const digits = new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2;
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(minor / 10 ** digits);
  } catch {
    return `${currency} ${(minor / 100).toFixed(2)}`;
  }
}
const moneyList = (m: Money) => (Object.keys(m).length ? Object.entries(m).map(([c, v]) => money(v, c)).join(' · ') : '—');
const reason = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** Is this the portal's address? `/partners` and anything under it. */
export const isPartnerPath = (pathname: string) => /^\/partners(\/|$)/.test(pathname);

/*
 * Google and Apple sign-in return to `/`, not here. Remember for the tab
 * that the portal is where to come back to — the invitation page does the same.
 */
const RETURN_KEY = 'drbell.partners-return';
export function rememberPartnersReturn() {
  try { sessionStorage.setItem(RETURN_KEY, String(Date.now())); } catch { /* code sign-in never leaves the page */ }
}
export function takePartnersReturn(): boolean {
  try {
    const at = Number(sessionStorage.getItem(RETURN_KEY));
    sessionStorage.removeItem(RETURN_KEY);
    return at > 0 && Date.now() - at < 30 * 60 * 1000;
  } catch {
    return false;
  }
}

export function PartnerPortal({ signedIn, onSignedIn }: { signedIn: boolean; onSignedIn: () => void }) {
  const [mine, setMine] = useState<Mine | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Destructive | null>(null);

  const load = useCallback(() => {
    if (!signedIn) return;
    api.get<Mine>('/api/partners/me').then(setMine).catch((e) => setError(reason(e)));
  }, [signedIn]);
  useEffect(load, [load]);

  const header = (
    <header className="mb-6 flex flex-wrap items-center gap-3 border-b border-(--line) pb-4">
      <Mark className="h-9 w-9 shrink-0" />
      <div className="min-w-0">
        <h1 className="text-2xl font-semibold tracking-tight">{mine?.partner?.displayName ?? 'DrBell partners'}</h1>
        <p className="text-sm text-(--muted)">Your link, what it brought, and what you have earned.</p>
      </div>
      <ThemeToggle className="ml-auto" />
      {signedIn && (
        <button
          className={button}
          onClick={() => setPending({
            title: 'Sign out of the partner portal?',
            body: 'Ends the session on this device only.',
            confirmLabel: 'Sign out',
            onConfirm: async () => { await api.post('/api/auth/sign-out'); location.reload(); },
          })}
        >
          Sign out
        </button>
      )}
    </header>
  );

  // Signed out here: if they choose Google or Apple, come back to the portal.
  useEffect(() => {
    if (!signedIn) rememberPartnersReturn();
  }, [signedIn]);

  if (!signedIn) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        {header}
        <SignIn title="DrBell partner portal" subtitle="Sign in with the phone number or email your partnership is under." onSignedIn={onSignedIn} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5 px-4 py-8">
      {header}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!mine ? <p className="text-(--muted)">Loading…</p> : (
        <>
          {mine.invitations.length > 0 && (
            <Invitations invitations={mine.invitations} setPending={setPending} onDone={load} hasPartner={Boolean(mine.partner)} />
          )}
          {!mine.partner ? (
            mine.invitations.length === 0 && (
              <section className="rounded-xl border border-(--line) p-4 text-sm">
                <p className="font-medium">This account is not a DrBell partner.</p>
                <p className="mt-1 text-(--muted)">
                  If a DrBell partner or our team has invited you, sign in with the phone number or email the invitation was
                  sent to. Otherwise, ask your DrBell contact about becoming a partner.
                </p>
              </section>
            )
          ) : (
            <Dashboard mine={mine} setPending={setPending} reload={load} />
          )}
        </>
      )}
      {pending && <Confirm {...pending} onCancel={() => setPending(null)} />}
    </div>
  );
}

function Invitations({ invitations, setPending, onDone, hasPartner }: {
  invitations: Invitation[]; setPending: (d: Destructive | null) => void; onDone: () => void; hasPartner: boolean;
}) {
  return (
    <section className="space-y-3 rounded-xl border border-(--accent) bg-(--accent-soft) p-4">
      <h2 className="font-semibold">You have been invited to be a DrBell partner</h2>
      {invitations.map((inv) => (
        <div key={inv.id} className="space-y-2 text-sm">
          <p>
            <strong>{inv.invitedBy}</strong> invited you as <strong>{inv.displayName}</strong>.{' '}
            {inv.depth === 1
              ? `You would earn ${pct(inv.sliceBps)} of the net revenue from subscribers you bring, out of ${inv.invitedBy}'s share — never on top of it.`
              : `You would earn ${pct(inv.poolBps)} of the net revenue from subscribers you and your sub-partners bring.`}
          </p>
          <p className="text-(--muted)">
            Commission is only ever on real subscription payments, never on sign-ups or on recruiting anyone. Nothing to
            pay to join. You will never see who your subscribers are — only numbers and money.
          </p>
          {hasPartner ? <p className="text-amber-600">This account is already a partner, so it cannot accept another partnership.</p> : (
            <div className="flex flex-wrap gap-2">
              <button
                className={primary}
                onClick={() => setPending({
                  title: `Become a DrBell partner as ${inv.displayName}?`,
                  body: 'You get your own link and code to share. Your display name is shown to people who use your code.',
                  confirmLabel: 'Accept',
                  onConfirm: async () => {
                    const r = await api.post<{ code: string }>(`/api/partners/invitations/${inv.id}/accept`);
                    setPending(null);
                    toast.success(`Welcome — your code is ${r.code}`);
                    onDone();
                  },
                })}
              >
                Accept
              </button>
              <button
                className={button}
                onClick={() => setPending({
                  title: 'Decline this invitation?',
                  body: `${inv.invitedBy} will see that it was declined. They can invite you again.`,
                  confirmLabel: 'Decline',
                  onConfirm: async () => {
                    await api.post(`/api/partners/invitations/${inv.id}/decline`);
                    setPending(null);
                    onDone();
                  },
                })}
              >
                Decline
              </button>
            </div>
          )}
        </div>
      ))}
    </section>
  );
}

const PERIODS = [
  ['30d', 'Last 30 days'],
  ['month', 'This month'],
  ['90d', 'Last 90 days'],
  ['year', 'This year'],
] as const;
type Period = (typeof PERIODS)[number][0];

function rangeOf(period: Period, now = Date.now()) {
  const d = new Date(now);
  const from =
    period === 'month' ? new Date(d.getFullYear(), d.getMonth(), 1).getTime()
      : period === 'year' ? new Date(d.getFullYear(), 0, 1).getTime()
        : now - (period === '30d' ? 30 : 90) * DAY;
  return { from, to: now + 60_000 };
}

function Dashboard({ mine, setPending, reload }: { mine: Mine; setPending: (d: Destructive | null) => void; reload: () => void }) {
  const p = mine.partner!;
  const [period, setPeriod] = useState<Period>('30d');
  const [summary, setSummary] = useState<Summary | null>(null);
  const [lines, setLines] = useState<Line[] | null>(null);

  useEffect(() => {
    const { from, to } = rangeOf(period);
    setSummary(null);
    api.get<Summary>(`/api/partners/me/summary?from=${from}&to=${to}`).then(setSummary).catch(() => setSummary(null));
  }, [period]);
  useEffect(() => {
    api.get<{ lines: Line[] }>('/api/partners/me/statement').then((r) => setLines(r.lines)).catch(() => setLines([]));
  }, []);

  return (
    <>
      {p.status !== 'active' && (
        <p className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
          Your partnership is <strong>{p.status}</strong>: your code does not credit new sign-ups at the moment.
        </p>
      )}
      {mine.link && <LinkCard link={mine.link} code={mine.codes.find((k) => k.link === mine.link)?.code ?? ''} name={p.displayName} />}

      <section className="rounded-xl border border-(--line) p-4">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <h2 className="font-semibold">What your link brought</h2>
          <select className={`${field} ml-auto`} value={period} onChange={(e) => setPeriod(e.target.value as Period)} aria-label="Period">
            {PERIODS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
        </div>
        {!summary ? <p className="text-sm text-(--muted)">Loading…</p> : (
          <div className="grid grid-cols-3 gap-3 text-center">
            {[['Link taps', summary.clicks], ['Sign-ups', summary.signUps], ['Paying subscribers', summary.subscribers]].map(([label, n]) => (
              <div key={String(label)} className="rounded-lg bg-(--panel) p-3">
                <div className="text-2xl font-semibold">{n}</div>
                <div className="text-xs text-(--muted)">{label}</div>
              </div>
            ))}
          </div>
        )}
        {summary && (
          <dl className="mt-4 grid grid-cols-[9rem_1fr] gap-y-1 text-sm">
            <dt className="text-(--muted)">On hold</dt><dd>{moneyList(summary.commission.pending)}</dd>
            <dt className="text-(--muted)">Approved</dt><dd>{moneyList(summary.commission.approved)}</dd>
            <dt className="text-(--muted)">Paid</dt><dd>{moneyList(summary.commission.paid)}</dd>
          </dl>
        )}
        <p className="mt-3 text-xs text-(--muted)">
          {p.commissionable
            ? `You earn ${p.depth === 0 ? pct(p.poolBps) : pct(p.sliceBps)} of the net revenue (after tax and the app store's fee) from each subscriber you bring, for ${p.durationMonths} months from when they joined. Commission is held for ${p.holdDays} days so refunds can land, then approved. Paid by ${p.payingEntity === 'newtact' ? 'Newtact Lab Private Limited' : 'Thaja FZE LLC'}.`
            : 'Your code records who you bring to DrBell. It does not earn commission.'}
        </p>
        {!mine.payoutsEnabled && (
          <p className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/5 p-2 text-xs">
            Payouts have not started yet. Your commission is recorded and approved as it falls due, and will be paid once
            DrBell's partner agreement and tax set-up are complete.
          </p>
        )}
      </section>

      {p.depth === 0 && <SubPartners poolBps={p.poolBps ?? 0} summary={summary} setPending={setPending} active={p.status === 'active'} />}

      <section className="rounded-xl border border-(--line) p-4">
        <h2 className="mb-2 font-semibold">Statement</h2>
        <p className="mb-2 text-xs text-(--muted)">Each subscriber is shown by a code like #A7F3 that only means something on this statement. DrBell never tells partners who their subscribers are.</p>
        {!lines ? <p className="text-sm text-(--muted)">Loading…</p> : lines.length === 0 ? <p className="text-sm text-(--muted)">Nothing yet. A line appears when somebody you brought pays for a plan.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-(--muted)"><tr><th className="p-1">Date</th><th className="p-1">Subscriber</th><th className="p-1">What</th><th className="p-1 text-right">You earn</th><th className="p-1">Status</th></tr></thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.id} className="border-t border-(--line)">
                    <td className="p-1">{day(l.at)}</td>
                    <td className="p-1 font-mono">{l.subscriber ?? '—'}</td>
                    <td className="p-1">
                      {l.kind === 'reversal' ? 'Refunded' : l.role === 'override' ? `Via ${l.via ?? 'a sub-partner'}` : 'Your subscriber'}
                      {l.tier ? ` · ${l.tier}` : ''} · {pct(l.rateBps)} of {money(l.basisMinor, l.currency)}
                    </td>
                    <td className={`p-1 text-right ${l.amountMinor < 0 ? 'text-red-600' : ''}`}>{money(l.amountMinor, l.currency)}</td>
                    <td className="p-1">{l.status === 'pending' ? `on hold until ${day(l.holdUntil)}` : l.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Aliases profile={p} contact={mine.contact} setPending={setPending} onDone={reload} />
    </>
  );
}

/** The link, the code in big type, copy, share, and a QR to print. */
function LinkCard({ link, code, name }: { link: string; code: string; name: string }) {
  const qrLink = `${link}?src=qr`;
  const qr = useMemo(() => encodeQr(qrLink), [qrLink]);
  const side = qr.size + 8;
  const share = `${name} recommends DrBell — medicine reminders that ring like an alarm, and tell your family if a dose is missed. ${link}`;

  const copy = async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${what} copied`);
    } catch {
      toast.error('Could not copy', 'Select it and copy it by hand.');
    }
  };
  const downloadQr = () => {
    const url = URL.createObjectURL(new Blob([qrSvg(qrLink)], { type: 'image/svg+xml' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `drbell-${code}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const shareNow = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'DrBell', text: share }); return; } catch { /* cancelled */ }
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(share)}`, '_blank', 'noopener');
  };

  return (
    <section className="grid gap-4 rounded-xl border border-(--line) p-4 sm:grid-cols-[1fr_auto]">
      <div className="min-w-0 space-y-2">
        <h2 className="font-semibold">Your link</h2>
        <p className="break-all font-mono text-sm">{link}</p>
        <p className="text-sm">Code: <span className="font-mono text-2xl font-semibold tracking-widest">{code}</span></p>
        <p className="text-xs text-(--muted)">Anyone can also type the code in the DrBell app when they sign up.</p>
        <div className="flex flex-wrap gap-2">
          <button className={primary} onClick={() => void shareNow()}>Share</button>
          <button className={button} onClick={() => void copy(link, 'Link')}>Copy link</button>
          <button className={button} onClick={() => void copy(code, 'Code')}>Copy code</button>
          <button className={button} onClick={downloadQr}>Download QR</button>
        </div>
      </div>
      <figure className="justify-self-center">
        <svg viewBox={`0 0 ${side} ${side}`} className="h-44 w-44 rounded bg-white" shapeRendering="crispEdges" role="img" aria-label={`QR code for ${link}`}>
          <rect width={side} height={side} fill="#ffffff" />
          <path d={qrSvgPath(qr)} fill="#000000" />
        </svg>
        <figcaption className="mt-1 text-center font-mono text-xs">Code: {code}</figcaption>
      </figure>
    </section>
  );
}

function SubPartners({ poolBps, summary, setPending, active }: {
  poolBps: number; summary: Summary | null; setPending: (d: Destructive | null) => void; active: boolean;
}) {
  const [subs, setSubs] = useState<Sub[] | null>(null);
  const [form, setForm] = useState({ displayName: '', contact: '', slice: '' });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get<{ subPartners: Sub[] }>('/api/partners/me/subpartners').then((r) => setSubs(r.subPartners)).catch(() => setSubs([]));
  }, []);
  useEffect(load, [load]);

  const invite = async () => {
    setBusy(true);
    const contact = form.contact.trim();
    const digits = contact.replace(/[^\d+]/g, '');
    const phone = contact.includes('@') ? undefined : digits.startsWith('+') ? digits : digits.length === 10 ? `+91${digits}` : `+${digits}`;
    try {
      await api.post('/api/partners/me/subpartners', {
        displayName: form.displayName.trim(),
        ...(contact.includes('@') ? { email: contact } : { phone }),
        sliceBps: Math.round(Number(form.slice) * 100),
      });
      toast.success('Invitation created', 'Ask them to sign in at this page with that phone number or email to accept.');
      setForm({ displayName: '', contact: '', slice: '' });
      load();
    } catch (e) {
      toast.error('Could not invite', e instanceof ApiError ? e.message : reason(e));
    } finally {
      setBusy(false);
    }
  };
  const sliceOk = form.slice !== '' && Number(form.slice) >= 0 && Number(form.slice) * 100 <= poolBps;
  const stats = (id: string) => summary?.subPartners.find((s) => s.id === id);

  return (
    <section className="rounded-xl border border-(--line) p-4">
      <h2 className="font-semibold">Sub-partners</h2>
      <p className="mb-3 text-xs text-(--muted)">
        A shop, a nurse or a staff member who shares DrBell for you. Their slice comes out of your {pct(poolBps)} — you keep
        the rest — and you earn nothing for inviting anyone, only from subscribers who pay.
      </p>
      {!subs ? <p className="text-sm text-(--muted)">Loading…</p> : subs.length === 0 ? <p className="text-sm text-(--muted)">None yet.</p> : (
        <ul className="space-y-2 text-sm">
          {subs.map((s) => {
            const st = stats(s.id);
            return (
              <li key={s.id} className="flex flex-wrap items-center gap-2 border-t border-(--line) pt-2">
                <span className="font-medium">{s.displayName}</span>
                <span className="text-xs text-(--muted)">{s.status === 'invited' ? `invited (${s.invitedTo})` : s.status} · slice {pct(s.sliceBps)}</span>
                {st && <span className="text-xs text-(--muted)">{st.signUps} sign-ups · {st.subscribers} paying · you earned {moneyList(st.yourShare)}</span>}
                {(s.status === 'active' || s.status === 'paused') && (
                  <button
                    className={`${button} ml-auto min-h-9 py-0 text-xs`}
                    onClick={() => setPending({
                      title: s.status === 'active' ? `Pause ${s.displayName}?` : `Resume ${s.displayName}?`,
                      body: s.status === 'active'
                        ? 'Their code stops crediting new sign-ups until you resume them.'
                        : 'Their code credits new sign-ups again.',
                      reassurance: 'Subscribers they already brought, and what has been earned on them, are unchanged.',
                      confirmLabel: s.status === 'active' ? 'Pause' : 'Resume',
                      onConfirm: async () => {
                        await api.put(`/api/partners/me/subpartners/${s.id}`, { status: s.status === 'active' ? 'paused' : 'active' });
                        setPending(null);
                        load();
                      },
                    })}
                  >
                    {s.status === 'active' ? 'Pause' : 'Resume'}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {active && (
        <div className="mt-4 grid gap-2 sm:grid-cols-[1fr_1fr_7rem_auto]">
          <input className={field} placeholder="Their name, as people will see it" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} aria-label="Sub-partner name" />
          <input className={field} placeholder="Their mobile or email" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} aria-label="Sub-partner phone or email" />
          <input className={field} type="number" min={0} max={poolBps / 100} step={0.5} placeholder={`Slice %`} value={form.slice} onChange={(e) => setForm({ ...form, slice: e.target.value })} aria-label="Slice percent" />
          <button className={primary} disabled={busy || form.displayName.trim().length < 2 || !form.contact.trim() || !sliceOk} onClick={() => void invite()}>Invite</button>
          {form.slice !== '' && !sliceOk && <p className="text-xs text-red-600 sm:col-span-4">The slice must be between 0 and {pct(poolBps)}.</p>}
        </div>
      )}
    </section>
  );
}

function Aliases({ profile, contact, setPending, onDone }: {
  profile: Profile; contact: Mine['contact']; setPending: (d: Destructive | null) => void; onDone: () => void;
}) {
  const flip = (which: 'phone' | 'email') => {
    const next = { phone: profile.phoneAlias, email: profile.emailAlias, [which]: !profile[which === 'phone' ? 'phoneAlias' : 'emailAlias'] };
    const on = next[which];
    setPending({
      title: on ? `Let people type your ${which} as your code?` : `Stop your ${which} working as a code?`,
      body: on
        ? `Anyone who types ${which === 'phone' ? contact?.phone ?? 'your number' : contact?.email ?? 'your email'} into DrBell will see your display name, "${profile.displayName}", and be credited to you.`
        : `Typing your ${which} will no longer credit you. Your code still works.`,
      confirmLabel: on ? 'Turn on' : 'Turn off',
      onConfirm: async () => {
        await api.put('/api/partners/me/aliases', next);
        setPending(null);
        onDone();
      },
    });
  };
  return (
    <section className="rounded-xl border border-(--line) p-4 text-sm">
      <h2 className="mb-1 font-semibold">Your number or email as a code</h2>
      <p className="mb-3 text-xs text-(--muted)">Off unless you turn it on. Useful when a customer cannot find your code but knows your number.</p>
      <div className="flex flex-wrap gap-2">
        {contact?.phone && <button className={button} onClick={() => flip('phone')}>{profile.phoneAlias ? `Phone ${contact.phone}: on — turn off` : `Phone ${contact.phone}: off — turn on`}</button>}
        {contact?.email && <button className={button} onClick={() => flip('email')}>{profile.emailAlias ? `Email: on — turn off` : `Email: off — turn on`}</button>}
      </div>
    </section>
  );
}
